// src/pages/Registrousuario.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getMessage } from "../../../data/locales";
import { API_URL } from "../../../config";
import {
  ROLES_ALTA_USUARIO,
  ROLES_ALTA_USUARIO_INSTRUCTOR,
  canAccessStaffUserRegistration,
  isAlumnoOnlyUserCreator,
} from "../../../utils/roles";
import "./Registrousuario.css";
import { newCursoRow } from "./cursoSelectionRow";
import { validateStaffRegistrationForm } from "../../../utils/validateStaffRegistrationForm";
import { uniqueCourseIdsFromRows } from "./registrousuarioCursosPayload";
import { TERMS_VERSION, PRIVACY_VERSION } from "../../../utils/consentVersions";
import {
  buildRegistrarAdminUsuarioFeedback,
  INVITE_REGISTRATION_SUCCESS_AUTO_CLEAR_MS,
  parseJsonFromResponse,
} from "../../../utils/registrarAdminUsuarioFeedback";
import { getBirthDateValidationError, getMaxBirthDateIso, getMinBirthDateIso } from "../../../utils/birthDateValidation";
import {
  IDENTITY_DOCUMENT_FORMAT_MESSAGE,
  IDENTITY_DOCUMENT_INPUT_PATTERN,
  normalizeIdentityDocumentInput,
} from "../../../utils/identityDocument";
import { scrollIntoViewSmooth } from "../../../utils/scrollIntoViewSmooth";
import PasswordVisibilityField from "../../../components/PasswordVisibilityField";
import ResponsiveActionSidebar from "../../../components/ResponsiveActionSidebar";
import ViewportSelect from "../../../components/ViewportSelect";

const CURSOS_CATALOG_DEFAULT_ERROR =
  "No se pudo cargar el listado de cursos. Revisa la conexión o inténtalo más tarde.";

function CursoFilasEditor({ rows, setRows, cursosDisponibles, selectAriaLabel, helpText, loadError = "" }) {
  const selectedIds = rows.map((r) => r.cursoId).filter(Boolean);
  const distinctSelectedCount = new Set(selectedIds).size;
  const totalCursos = cursosDisponibles.length;
  const allCoursesSelected =
    totalCursos > 0 && distinctSelectedCount >= totalCursos;

  return (
    <>
      {helpText ? <p className="regusu-cursos-help">{helpText}</p> : null}
      {loadError ? (
        <p className="regusu-cursos-help regusu-cursos-load-error" role="alert">
          {loadError}
        </p>
      ) : null}
      <ul className="regusu-cursos-list">
        {rows.map((row) => {
          const takenElsewhere = new Set(
            rows
              .filter((r) => r.id !== row.id && r.cursoId !== "")
              .map((r) => r.cursoId)
          );
          return (
            <li key={row.id} className="regusu-curso-row">
              <ViewportSelect
                className="form-control curso-dropdown"
                value={row.cursoId}
                onChange={(e) => {
                  const value = e.target.value;
                  setRows((prev) =>
                    prev.map((r) => (r.id === row.id ? { ...r, cursoId: value } : r))
                  );
                }}
                aria-label={selectAriaLabel}
              >
                <option value="">Selecciona un curso</option>
                {cursosDisponibles.map((cursoItem) => {
                  const cid = String(cursoItem._id);
                  const disabled = takenElsewhere.has(cid) && row.cursoId !== cid;
                  return (
                    <option key={cid} value={cid} disabled={disabled}>
                      {cursoItem.titulo}
                      {disabled ? " (ya elegido)" : ""}
                    </option>
                  );
                })}
              </ViewportSelect>
              <button
                type="button"
                className="regusu-curso-remove"
                onClick={() => {
                  setRows((prev) => {
                    if (prev.length <= 1) {
                      return [{ ...prev[0], cursoId: "" }];
                    }
                    return prev.filter((r) => r.id !== row.id);
                  });
                }}
                aria-label="Quitar esta fila de curso"
              >
                Quitar
              </button>
            </li>
          );
        })}
      </ul>
      {!allCoursesSelected ? (
        <button
          type="button"
          className="regusu-curso-add"
          onClick={() => setRows((prev) => [...prev, newCursoRow()])}
        >
          Añadir curso
        </button>
      ) : null}
    </>
  );
}

const Registrousuario = () => {
  const navigate = useNavigate();
  const registroAutoClearRef = useRef(null);
  const inviteAutoClearRef = useRef(null);
  const [mensaje, setMensaje] = useState("");
  const [registroScrollTick, setRegistroScrollTick] = useState(0);
  const registroMessageRef = useRef(null);
  const [registroMsgKind, setRegistroMsgKind] = useState("");
  const [registroSubmitting, setRegistroSubmitting] = useState(false);
  const [showAllPasswords, setShowAllPasswords] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    return () => {
      if (registroAutoClearRef.current != null) {
        window.clearTimeout(registroAutoClearRef.current);
        registroAutoClearRef.current = null;
      }
      if (inviteAutoClearRef.current != null) {
        window.clearTimeout(inviteAutoClearRef.current);
        inviteAutoClearRef.current = null;
      }
    };
  }, []);

  // Estados para los campos del formulario
  const [dni, setDni] = useState("");
  const [nombre, setNombre] = useState("");
  const [primerApellido, setPrimerApellido] = useState("");
  const [segundoApellido, setSegundoApellido] = useState("");
  const [fechaNacimiento, setFechaNacimiento] = useState("");
  const [direccion, setDireccion] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [contraseña, setContraseña] = useState("");
  const [contraseñaConfirmacion, setContraseñaConfirmacion] = useState("");
  const [rol, setRol] = useState("");
  const [cursoRows, setCursoRows] = useState(() => [newCursoRow()]);
  const [cursosDisponibles, setCursosDisponibles] = useState([]);
  const [cursosLoadError, setCursosLoadError] = useState("");
  const [currentUserRol, setCurrentUserRol] = useState(null);
  const [roleLoaded, setRoleLoaded] = useState(false);
  const [delegatedCreateUsers, setDelegatedCreateUsers] = useState(false);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteCursoRows, setInviteCursoRows] = useState(() => [newCursoRow()]);
  const [inviteConsent, setInviteConsent] = useState(false);
  const [mensajeInvitacion, setMensajeInvitacion] = useState("");
  const [inviteMsgKind, setInviteMsgKind] = useState("");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);

  const roleKnown =
    roleLoaded &&
    typeof currentUserRol === "string" &&
    currentUserRol.trim() !== "" &&
    canAccessStaffUserRegistration(currentUserRol, delegatedCreateUsers);

  const instructorOnly = roleKnown && isAlumnoOnlyUserCreator(currentUserRol, delegatedCreateUsers);

  const roleOptions = !roleKnown
    ? []
    : instructorOnly
      ? ROLES_ALTA_USUARIO_INSTRUCTOR
      : ROLES_ALTA_USUARIO;

  /** Empty until role is verified so submit stays invalid; instructor always sends alumno. */
  const rolParaEnvio = !roleKnown ? "" : instructorOnly ? "alumno" : rol;
  const valorSelectRol = !roleKnown ? "" : instructorOnly ? "alumno" : rol;

  /**
   * Course picker source: /cursos follows the logged-in user (RBAC on the server).
   * Use it when the staff member is an instructor (alumno + invite flows) or when
   * the form target role is instructor (director assigning admin courses). Otherwise
   * use the public catalog for alumno/director targets created by platform staff.
   */
  const useManagementCourseList =
    roleKnown && (currentUserRol === "instructor" || rol === "instructor");

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/verificar-token`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const rUser = data?.usuario?.rol ?? null;
        setCurrentUserRol(rUser);
        setDelegatedCreateUsers(data?.usuario?.permisos?.puedeCrearUsuarios === true);
      })
      .catch(() => {
        if (!cancelled) setCurrentUserRol(null);
      })
      .finally(() => {
        if (!cancelled) setRoleLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!roleKnown) {
      setCursosDisponibles([]);
      setCursosLoadError("");
      return undefined;
    }
    let cancelled = false;
    const url = useManagementCourseList
      ? `${API_URL}/cursos`
      : `${API_URL}/cursos-publicos`;
    const fetchCursos = async () => {
      try {
        const response = await fetch(url, {
          method: "GET",
          credentials: "include",
        });
        let data = null;
        try {
          data = await response.json();
        } catch {
          data = null;
        }
        if (cancelled) return;
        if (!response.ok) {
          setCursosDisponibles([]);
          const fromBody =
            data !== null &&
            typeof data === "object" &&
            typeof data.error === "string" &&
            data.error.trim() !== ""
              ? data.error.trim()
              : "";
          setCursosLoadError(fromBody || CURSOS_CATALOG_DEFAULT_ERROR);
          return;
        }
        const list = Array.isArray(data) ? data : [];
        setCursosDisponibles(list);
        setCursosLoadError("");
      } catch (error) {
        if (!cancelled) {
          console.error("Error al obtener cursos", error);
          setCursosDisponibles([]);
          setCursosLoadError(CURSOS_CATALOG_DEFAULT_ERROR);
        }
      }
    };
    fetchCursos();
    return () => {
      cancelled = true;
    };
  }, [roleKnown, useManagementCourseList]);

  useEffect(() => {
    if (registroScrollTick === 0 || !mensaje) {
      return;
    }
    scrollIntoViewSmooth(registroMessageRef.current);
  }, [mensaje, registroScrollTick]);

  const queueRegistroScroll = () => {
    setRegistroScrollTick((tick) => tick + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (registroAutoClearRef.current != null) {
      window.clearTimeout(registroAutoClearRef.current);
      registroAutoClearRef.current = null;
    }
    setMensaje("");
    setRegistroMsgKind("");

    const effectiveRol = rolParaEnvio;

    const validationError = validateStaffRegistrationForm({
      dni,
      nombre,
      primerApellido,
      telefono,
      email,
      rol: effectiveRol,
      contraseña,
      contraseñaConfirmacion,
      cursoIds: uniqueCourseIdsFromRows(cursoRows),
    });
    if (validationError) {
      setRegistroMsgKind("error");
      setMensaje(validationError);
      queueRegistroScroll();
      return;
    }

    const birthDateError = getBirthDateValidationError(fechaNacimiento);
    if (birthDateError) {
      setRegistroMsgKind("error");
      setMensaje(birthDateError);
      queueRegistroScroll();
      return;
    }

    const payload = {
      dni,
      nombre,
      primerApellido,
      segundoApellido,
      fechaNacimiento,
      direccion,
      telefono,
      email,
      rol: effectiveRol,
      cursosInscritos:
        effectiveRol === "instructor" || effectiveRol === "alumno"
          ? uniqueCourseIdsFromRows(cursoRows)
          : [],
      contraseña:
        typeof contraseña === "string" && contraseña.trim().length > 0
          ? contraseña.trim()
          : contraseña,
    };

    setRegistroSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/usuarios/registrar-y-enviar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await parseJsonFromResponse(response);
      const feedback = buildRegistrarAdminUsuarioFeedback(response.ok, data);
      setRegistroMsgKind(feedback.kind);
      setMensaje(feedback.lines.join("\n\n"));
      queueRegistroScroll();

      if (response.ok) {
        setDni("");
        setNombre("");
        setPrimerApellido("");
        setSegundoApellido("");
        setFechaNacimiento("");
        setDireccion("");
        setTelefono("");
        setEmail("");
        setContraseña("");
        setContraseñaConfirmacion("");
        setRol(instructorOnly ? "alumno" : "");
        setCursoRows([newCursoRow()]);

        if (feedback.autoClearMs > 0) {
          if (registroAutoClearRef.current != null) {
            window.clearTimeout(registroAutoClearRef.current);
            registroAutoClearRef.current = null;
          }
          registroAutoClearRef.current = window.setTimeout(() => {
            registroAutoClearRef.current = null;
            setMensaje("");
            setRegistroMsgKind("");
          }, feedback.autoClearMs);
        }
      }
    } catch (error) {
      console.error("❌ Error en el registro:", error);
      setRegistroMsgKind("error");
      setMensaje("Error en el servidor. Intenta de nuevo.");
      queueRegistroScroll();
    } finally {
      setRegistroSubmitting(false);
    }
  };

  const handleInvitePago = async (e) => {
    e.preventDefault();
    if (inviteAutoClearRef.current != null) {
      window.clearTimeout(inviteAutoClearRef.current);
      inviteAutoClearRef.current = null;
    }
    setMensajeInvitacion("");
    setInviteMsgKind("");
    if (!roleKnown) {
      setInviteMsgKind("error");
      setMensajeInvitacion("No se ha verificado tu rol en la plataforma. Recarga la página o vuelve a iniciar sesión.");
      document.getElementById("regusu-invite-anchor")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    const ids = uniqueCourseIdsFromRows(inviteCursoRows);
    if (ids.length === 0) {
      setInviteMsgKind("error");
      setMensajeInvitacion("Selecciona al menos un curso para el pago.");
      document.getElementById("regusu-invite-anchor")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    if (!inviteConsent) {
      setInviteMsgKind("error");
      setMensajeInvitacion("Debes marcar la casilla de confirmación para continuar.");
      document.getElementById("regusu-invite-anchor")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    setInviteSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/pedidos/invitar-pago-alumno`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          alumnoEmail: inviteEmail.trim(),
          items: ids.map((cursoId) => ({ cursoId, qty: 1 })),
          consentAccepted: true,
          termsVersion: TERMS_VERSION,
          policyVersion: PRIVACY_VERSION,
        }),
      });
      const data = await parseJsonFromResponse(response);
      if (response.ok) {
        setInviteMsgKind(data.emailSent === false ? "warning" : "success");
        const base =
          typeof data.mensaje === "string" && data.mensaje.trim() !== ""
            ? data.mensaje.trim()
            : data.emailSent === false
              ? "Pedido creado."
              : "Pedido creado y correo enviado al alumno.";
        const orderRef =
          data.orderId != null && String(data.orderId).trim() !== ""
            ? `\n\nReferencia del pedido: ${String(data.orderId).trim()}.`
            : "";
        const emailLine =
          data.emailSent === false
            ? "\n\nNo se ha podido confirmar el envío del correo; revisa el registro de actividad o la bandeja del alumno."
            : "\n\nEl alumno debe iniciar sesión y completar el pago desde el enlace recibido.";
        setMensajeInvitacion(`${base}${orderRef}${emailLine}`);
        setInviteEmail("");
        setInviteCursoRows([newCursoRow()]);
        setInviteConsent(false);
        if (inviteAutoClearRef.current != null) {
          window.clearTimeout(inviteAutoClearRef.current);
          inviteAutoClearRef.current = null;
        }
        if (data.emailSent !== false) {
          inviteAutoClearRef.current = window.setTimeout(() => {
            inviteAutoClearRef.current = null;
            setMensajeInvitacion("");
            setInviteMsgKind("");
          }, INVITE_REGISTRATION_SUCCESS_AUTO_CLEAR_MS);
        }
      } else {
        setInviteMsgKind("error");
        setMensajeInvitacion(
          typeof data.error === "string" && data.error.trim()
            ? data.error.trim()
            : "No se pudo enviar la invitación al pago.",
        );
      }
      document.getElementById("regusu-invite-anchor")?.scrollIntoView({ behavior: "smooth" });
    } catch (err) {
      console.error("invite pago:", err);
      setInviteMsgKind("error");
      setMensajeInvitacion("Error en el servidor. Intenta de nuevo.");
      document.getElementById("regusu-invite-anchor")?.scrollIntoView({ behavior: "smooth" });
    } finally {
      setInviteSubmitting(false);
    }
  };

  return (
    <div className="regusu-container">
      
      <div className="regusu-main-container">
        <div className="regusu-content">
          <h1>
            {roleKnown && instructorOnly
              ? "Registrar usuarios"
              : "Usuarios: cuenta y pagos"}
          </h1>
          {!roleKnown ? (
            <p className="regusu-page-lead">
              {!roleLoaded
                ? "Verificando tu rol en la plataforma…"
                : "No se pudo verificar tu rol. Recarga la página o vuelve a iniciar sesión. Los formularios permanecen desactivados hasta entonces."}
            </p>
          ) : !instructorOnly ? (
            <p className="regusu-page-lead">
              <strong>Dos pasos distintos:</strong> primero creas la <strong>cuenta</strong> (datos y rol). Para que un
              <strong> alumno</strong> compre cursos, cuando ya tenga cuenta y correo verificado, usa el segundo bloque{" "}
              <strong>Cursos y pago</strong> (pedido e invitación por correo). Dar de alta un instructor y asignarle
              cursos como administrador solo aplica en el primer bloque.
            </p>
          ) : (
            <p className="regusu-page-lead">
              <strong>Dos bloques:</strong> en el primero das de alta la <strong>cuenta del alumno</strong> (datos, rol
              alumno y cursos del pedido inicial). En el segundo, si el alumno ya tenía cuenta creada por dirección y
              correo verificado, puedes generar solo el <strong>pedido e invitación al pago</strong>.
            </p>
          )}
          {mensaje ? (
            <p
              ref={registroMessageRef}
              id="regusu-registro-message"
              className={`regusu-message${
                registroMsgKind === "error"
                  ? " regusu-message--error"
                  : registroMsgKind === "warning"
                    ? " regusu-message--warning"
                    : registroMsgKind === "success"
                      ? " regusu-message--success"
                      : ""
              }`}
              role="status"
              aria-live="polite"
            >
              {mensaje}
            </p>
          ) : null}
          <section className="regusu-section" aria-labelledby="regusu-section-cuenta-title">
            <h2 id="regusu-section-cuenta-title" className="regusu-section-title">
              1. Crear cuenta en la aplicación
            </h2>
            <p className="regusu-section-desc">
              {!roleKnown
                ? !roleLoaded
                  ? "Cargando tu rol en la plataforma… Los campos del formulario están desactivados hasta entonces."
                  : "No se ha podido determinar un rol válido para esta página. Recarga o vuelve a iniciar sesión; el formulario permanece desactivado."
                : instructorOnly
                  ? "Alta de alumno: rol fijo «Alumno». Indica los cursos del pedido inicial; se envía correo de verificación. No puedes crear directores ni instructores desde aquí."
                  : "Si el rol es alumno, elige los cursos del pedido inicial; el alumno verifica el correo. Si es instructor, elige cursos donde será administrador. Si es director de plataforma, no se asignan cursos en este formulario."}
            </p>
            <form id="registro-usuario-form" onSubmit={handleSubmit} className="regusu-form">
              <fieldset disabled={!roleKnown} className="regusu-fieldset-registro">
              <div className="form-group">
                <label>DNI o NIE:</label>
                <input
                  type="text"
                  maxLength={9}
                  className="form-control"
                  value={dni}
                  onChange={(e) => setDni(normalizeIdentityDocumentInput(e.target.value))}
                  pattern={IDENTITY_DOCUMENT_INPUT_PATTERN}
                  placeholder="12345678A"
                  title={IDENTITY_DOCUMENT_FORMAT_MESSAGE}
                  required
                />
              </div>
              <div className="form-group">
                <label>Nombre:</label>
                <input
                  type="text"
                  maxLength={20}
                  className="form-control"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Primer Apellido:</label>
                <input
                  type="text"
                  maxLength={20}
                  className="form-control"
                  value={primerApellido}
                  onChange={(e) => setPrimerApellido(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Segundo Apellido:</label>
                <input
                  type="text"
                  maxLength={20}
                  className="form-control"
                  value={segundoApellido}
                  onChange={(e) => setSegundoApellido(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Fecha de Nacimiento:</label>
                <input
                  type="date"
                  className="form-control"
                  value={fechaNacimiento}
                  onChange={(e) => setFechaNacimiento(e.target.value)}
                  min={getMinBirthDateIso()}
                  max={getMaxBirthDateIso()}
                  required
                />
              </div>
              <div className="form-group">
                <label>Dirección:</label>
                <input
                  type="text"
                  maxLength={80}
                  className="form-control"
                  value={direccion}
                  onChange={(e) => setDireccion(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Teléfono:</label>
                <input
                  type="text"
                  maxLength={9}
                  className="form-control"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  placeholder="123456789"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  maxLength={40}
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="regusu-password">Contraseña:</label>
                <PasswordVisibilityField
                  id="regusu-password"
                  value={contraseña}
                  autoComplete="new-password"
                  onChange={(e) => {
                    setContraseña(e.target.value);
                  }}
                  visible={showAllPasswords || showPassword}
                  onToggleVisible={() => {
                    if (showAllPasswords) setShowAllPasswords(false);
                    else setShowPassword((visible) => !visible);
                  }}
                  placeholder={
                    rol === "director" || rol === "instructor"
                      ? "Obligatoria para director/instructor"
                      : "(Opcional: se genera una aleatoria)"
                  }
                  required={rol === "director" || rol === "instructor"}
                  maxLength={128}
                />
              </div>
              <div className="form-group">
                <label htmlFor="regusu-confirm-password">Confirmar contraseña:</label>
                <PasswordVisibilityField
                  id="regusu-confirm-password"
                  value={contraseñaConfirmacion}
                  autoComplete="new-password"
                  onChange={(e) => setContraseñaConfirmacion(e.target.value)}
                  visible={showAllPasswords || showConfirmPassword}
                  onToggleVisible={() => {
                    if (showAllPasswords) setShowAllPasswords(false);
                    else setShowConfirmPassword((visible) => !visible);
                  }}
                  showLabelKey="resetPassword.showConfirm"
                  hideLabelKey="resetPassword.hideConfirm"
                  required={rol === "director" || rol === "instructor"}
                  maxLength={128}
                />
              </div>
              <div className="form-group">
                <label htmlFor="regusu-nuevo-usuario-rol">Rol:</label>
                <ViewportSelect
                  id="regusu-nuevo-usuario-rol"
                  className="form-control"
                  value={valorSelectRol}
                  onChange={(e) => {
                    setRol(e.target.value);
                    setCursoRows([newCursoRow()]);
                  }}
                  required={roleKnown && !instructorOnly}
                  disabled={!roleKnown || instructorOnly}
                  aria-busy={!roleLoaded}
                  aria-label="Rol del nuevo usuario"
                >
                  {!roleKnown ? (
                    <option value="" disabled>
                      {!roleLoaded ? "Cargando roles…" : "Rol no disponible"}
                    </option>
                  ) : !instructorOnly ? (
                    <option value="" disabled>
                      Selecciona un rol
                    </option>
                  ) : null}
                  {roleOptions.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </ViewportSelect>
              </div>
              {rol === "director" ? (
                <p className="regusu-director-hint">
                  El perfil director tiene acceso global a todos los cursos de la plataforma. No es necesario
                  asignar cursos manualmente en este formulario.
                </p>
              ) : null}
              {rol === "instructor" ? (
                <div className="form-group regusu-cursos-field">
                  <label>Cursos como administrador del instructor:</label>
                  <CursoFilasEditor
                    rows={cursoRows}
                    setRows={setCursoRows}
                    cursosDisponibles={cursosDisponibles}
                    loadError={cursosLoadError}
                    selectAriaLabel="Curso en el que el instructor será administrador"
                    helpText="Solo aplica al rol instructor: se añade como administrador de cada curso elegido. Alumnos y directores no reciben cursos aquí; el pago de alumnos va por «Invitar al pago»."
                  />
                </div>
              ) : null}
              {rolParaEnvio === "alumno" ? (
                <div className="form-group regusu-cursos-field">
                  <label>Cursos del pedido inicial (alumno):</label>
                  <CursoFilasEditor
                    rows={cursoRows}
                    setRows={setCursoRows}
                    cursosDisponibles={cursosDisponibles}
                    loadError={cursosLoadError}
                    selectAriaLabel="Curso para el alta del alumno"
                    helpText="Al menos un curso. Los gratuitos se asignan al verificar el correo; los de pago quedan en pedido pendiente hasta que pague."
                  />
                </div>
              ) : null}
              </fieldset>
            </form>
          </section>

          <div
            id="regusu-invite-anchor"
            className={`regusu-section regusu-invite-section${instructorOnly ? " regusu-invite-section--solo" : ""}`}
          >
            <h2 className="regusu-invite-title" id="regusu-section-pago-title">
              {instructorOnly ? "2. Invitación al pago (alumno ya registrado)" : "2. Cursos y pago del alumno"}
            </h2>
            <p className="regusu-section-desc">
              {!roleKnown
                ? !roleLoaded
                  ? "Esperando verificación de tu rol… Los campos de este bloque están desactivados hasta entonces."
                  : "No se ha podido verificar tu rol. Recarga la página o vuelve a iniciar sesión; este bloque permanece desactivado."
                : instructorOnly
                  ? "Solo si el alumno ya tiene cuenta (por ejemplo creada por dirección) y el correo verificado: generas el pedido y se envía la invitación para iniciar sesión y pagar."
                  : "Solo para alumnos que ya tienen cuenta en la plataforma. Indica su correo y los cursos del pedido; se envía la invitación para que complete el pago tras iniciar sesión."}
            </p>
            {mensajeInvitacion ? (
              <p
                className={`regusu-message regusu-message--invite${
                  inviteMsgKind === "error"
                    ? " regusu-message--error"
                    : inviteMsgKind === "warning"
                      ? " regusu-message--warning"
                      : inviteMsgKind === "success"
                        ? " regusu-message--success"
                        : ""
                }`}
                role="status"
                aria-live="polite"
              >
                {mensajeInvitacion}
              </p>
            ) : null}
            <form id="invitar-pago-alumno-form" onSubmit={handleInvitePago} className="regusu-form">
              <fieldset disabled={!roleKnown} className="regusu-fieldset-registro">
                <div className="form-group">
                  <label htmlFor="regusu-invite-email">Correo del alumno:</label>
                  <input
                    id="regusu-invite-email"
                    type="email"
                    className="form-control"
                    maxLength={120}
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    autoComplete="off"
                  />
                </div>
                <div className="form-group regusu-cursos-field">
                  <label>Cursos a incluir en el pedido:</label>
                  <CursoFilasEditor
                    rows={inviteCursoRows}
                    setRows={setInviteCursoRows}
                    cursosDisponibles={cursosDisponibles}
                    loadError={cursosLoadError}
                    selectAriaLabel="Curso para el pedido de pago del alumno"
                    helpText={
                      instructorOnly
                        ? "Solo aparecen los cursos en los que eres administrador. Añade tantas filas como necesites."
                        : useManagementCourseList
                          ? "Misma lista de cursos gestionables que en el bloque 1 (según tu rol en la plataforma). Añade tantas filas como necesites."
                          : "Lista de cursos públicos activos. Añade tantas filas como necesites."
                    }
                  />
                </div>
                <div className="form-group regusu-consent-row">
                  <label className="regusu-consent-label">
                    <input
                      type="checkbox"
                      checked={inviteConsent}
                      onChange={(e) => setInviteConsent(e.target.checked)}
                    />
                    <span>
                      Confirmo que el alumno ha sido informado y que acepta las condiciones según los{" "}
                      <Link
                        to="/terminos"
                        target="_blank"
                        rel="noopener noreferrer"
                        title={getMessage("legal.opensInNewTab")}
                        onClick={(event) => event.stopPropagation()}
                      >
                        términos
                      </Link>
                      {" "}y la{" "}
                      <Link
                        to="/privacidad"
                        target="_blank"
                        rel="noopener noreferrer"
                        title={getMessage("legal.opensInNewTab")}
                        onClick={(event) => event.stopPropagation()}
                      >
                        política de privacidad
                      </Link>
                      {" "}vigentes en la plataforma.
                    </span>
                  </label>
                </div>
              </fieldset>
            </form>
          </div>
        </div>
        <ResponsiveActionSidebar
          id="registro-usuario-sidebar"
          className="regusu-sidebar"
          openClassName="regusu-sidebar--open"
          ariaLabel="Acciones de registro de usuario"
        >
          <h4>Acciones</h4>
          <ul className="regusu-sidebar-links">
            {roleKnown ? (
              <li>
                <button
                  type="submit"
                  form="registro-usuario-form"
                  className="regusu-sidebar-button"
                  disabled={registroSubmitting}
                >
                  {registroSubmitting
                    ? "Guardando…"
                    : instructorOnly
                      ? "Registrar usuario"
                      : "Guardar cuenta (bloque 1)"}
                </button>
              </li>
            ) : null}
            <li>
              <button
                type="submit"
                form="invitar-pago-alumno-form"
                className="regusu-sidebar-button"
                disabled={!roleKnown || inviteSubmitting}
              >
                {inviteSubmitting
                  ? "Enviando invitación…"
                  : "Enviar invitación al pago (bloque 2)"}
              </button>
            </li>
            {roleKnown ? (
              <li>
                <button type="button" className="regusu-sidebar-button" onClick={() => setShowAllPasswords(!showAllPasswords)}>
                  {showAllPasswords ? "Ocultar contraseñas" : "Mostrar contraseñas"}
                </button>
              </li>
            ) : null}
            <li>
              <button
                className="perf-sidebar-button staff-sidebar-exit-button staff-sidebar-exit-button--discard"
                onClick={() => navigate(-1)}
              >
                 Cancelar y salir
              </button>
            </li>
          </ul>
        </ResponsiveActionSidebar>
      </div>
    </div>
  );
};

export default Registrousuario;
